import React, { useEffect, useState } from "react";
import { Box, CardContent, Typography, Grid, IconButton } from "@mui/material";
import { AppDispatch, RootReducerType, RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchUploadedDocuments } from "../application-preview/redux/thunk/UploadDocumentsThunk";
import {
  deleteFileByIdThunk,
  getDeleteSignedURLThunk,
  getFileSignedURLThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { app } from "@config/firebaseConfig";

export const ImagePreviewGridRow: React.FC<{
  refreshTrigger?: boolean;
  onDeleteSuccess?: () => void;
  visibleIcons?: boolean;
}> = ({ refreshTrigger, onDeleteSuccess, visibleIcons = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { access_id, category_id } = useSelector(
    (state: RootReducerType) => state.amenitiesROW,
  );

  const applicationCategoryId = useSelector(
    (state: RootReducerType) =>
      state.applicationDetailsROW.applicationDetailsROW
        ?.application_category_id,
  );

  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );
  const { uploadDocuments } = useSelector(
    (state: RootState) => state.uploadedDocuments,
  );

  const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    dispatch(
      fetchUploadedDocuments({
        access_id: String(applicationEditId || access_id),
        amenityCategory: applicationCategoryId || category_id,
      }),
    );
    setImageUrls({});
  }, [dispatch, access_id, category_id, applicationCategoryId, refreshTrigger]);

  const imageDocs =
    uploadDocuments?.files?.filter(
      (doc: any) =>
        doc.document_type === "FILE_UPLOAD" &&
        doc.media_type?.startsWith("image"),
    ) || [];

  useEffect(() => {
    if (imageDocs.length === 0) return;

    imageDocs.forEach(async (doc: any) => {
      if (!imageUrls[doc.document_name]) {
        const result = await dispatch(
          getFileSignedURLThunk({ document_name: doc.document_name }),
        );
        const signedUrl = result.payload?.data?.signedUrl;

        if (signedUrl) {
          setImageUrls((prev) => ({
            ...prev,
            [doc.document_name]: signedUrl,
          }));
        }
      }
    });
  }, [dispatch, imageDocs, imageUrls]);

  if (!uploadDocuments) return null;

  const getGridSize = (count: number) => {
    if (count >= 4) return 3; // 12/3 = 4 per row
    if (count === 3) return 4; // 12/4 = 3 per row
    if (count === 2) return 6; // 12/6 = 2 per row
    return 12; // 1 per row
  };

  return (
    <CardContent sx={{ p: 0 }}>
      <Grid container spacing={2}>
        {/* {imageDocs.length === 0 && (
                    <Typography>No uploaded images found.</Typography>
                )} */}
        {imageDocs.map((doc: any, idx: number) => (
          <Grid
            item
            xs={12}
            sm={getGridSize(imageDocs.length)}
            md={4}
            key={doc.document_name || idx}
          >
            <Box
              sx={{
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 1,
              }}
            >
              {imageUrls[doc.document_name] ? (
                <>
                  <Box
                    component="img"
                    src={imageUrls[doc.document_name]}
                    alt={doc.document_name}
                    sx={{
                      width: "100%",
                      objectFit: "fill",
                      aspectRatio: "16/9",
                      display: "block",
                    }}
                  />
                  {visibleIcons && (
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "#00000060",
                        borderRadius: "5px",
                        border: "1px solid #D0D5DD",
                        padding: "4px",
                        zIndex: 1,
                        "&:hover": {
                          backgroundColor: "#00000027",
                        },
                      }}
                      onClick={async () => {
                        try {
                          const deleteSignURL = await dispatch(
                            getDeleteSignedURLThunk(doc.document_name),
                          );
                          const signedUrl =
                            deleteSignURL.payload?.data?.signedUrl;
                          if (
                            deleteSignURL.meta.requestStatus === "fulfilled"
                          ) {
                            await deleteFileOnAwsAPI(signedUrl);
                            await dispatch(
                              deleteFileByIdThunk({
                                access_id: applicationEditId || access_id,
                                document_id: doc.id,
                              }),
                            );
                            if (onDeleteSuccess) onDeleteSuccess();
                          }
                        } catch (error) {
                          console.error("Error deleting file:", error);
                        }
                      }}
                    >
                      <DeleteOutlineOutlinedIcon
                        style={{ color: "#fff" }}
                        fontSize="small"
                      />
                    </IconButton>
                  )}
                </>
              ) : (
                <Typography variant="caption" sx={{ textAlign: "center" }}>
                  Loading...
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  );
};
